import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FileDown, ArrowLeft, Package, X, Filter, Eye, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { fetchIndentOpen, fetchReceiptStats } from '../../../store/dashboardSlice';
import Header from '../../../components/Header/Header';
import Footer from '../../../components/Footer/Footer';
import { ExportPopup } from './ExportPopup';
import { format } from 'date-fns';
import IndentDetails from './IndentDetails';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import Modal from 'react-modal';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

// Helper function to get status counts
const getStatusCounts = (items) => {
  if (!items || !Array.isArray(items)) return { pending: 0, partial: 0, total: 0 };
  return items.reduce((acc, item) => {
    if (item.ReceiptStatus === "Pending") acc.pending++;
    if (item.ReceiptStatus === "Partially Received") acc.partial++;
    acc.total++;
    return acc;
  }, { pending: 0, partial: 0, total: 0 });
};

// Category Filter Modal Component
const CategoryFilterModal = ({ isOpen, onClose, onApply, selectedCategories, setSelectedCategories }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/dropdown/mcategories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCategories.length === categories.length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(categories.map(cat => cat.CategoryID));
    }
  };

  const handleApply = () => {
    onApply();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-96 overflow-hidden">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Filter by Categories</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            <div className="mb-4">
              <button
                onClick={handleSelectAll}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {selectedCategories.length === categories.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            
            <div className="max-h-48 overflow-y-auto mb-4">
              {categories.map((category) => (
                <label key={category.CategoryID} className="flex items-center mb-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(category.CategoryID)}
                    onChange={() => handleCategoryToggle(category.CategoryID)}
                    className="mr-3 h-4 w-4 text-blue-600"
                  />
                  <span className="text-sm">{category.CategoryName}</span>
                </label>
              ))}
            </div>
            
            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Apply Filter
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ReceiptStatsBar component
const ReceiptStatsBar = ({ items, onFilterChange, selectedCategories, onCategoryFilter }) => {
  const { pending, partial, total } = getStatusCounts(items);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [tempSelectedCategories, setTempSelectedCategories] = useState(selectedCategories);

  const handleCategoryApply = () => {
    onCategoryFilter(tempSelectedCategories);
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-blue-600 font-medium">Total Items</p>
            <p className="text-2xl font-bold text-blue-700">{total}</p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <p className="text-sm text-yellow-600 font-medium">Pending</p>
            <p className="text-2xl font-bold text-yellow-700">{pending}</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-sm text-green-600 font-medium">Partially Received</p>
            <p className="text-2xl font-bold text-green-700">{partial}</p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => onFilterChange("status", "all")}
            className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
          >
            Show All
          </button>
          <button
            onClick={() => onFilterChange("status", "pending")}
            className="px-3 py-1 text-sm bg-yellow-100 text-yellow-700 rounded-md hover:bg-yellow-200"
          >
            Show Pending
          </button>
          <button
            onClick={() => onFilterChange("status", "partial")}
            className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200"
          >
            Show Partially Received
          </button>
          <button
            onClick={() => setShowCategoryModal(true)}
            className={`px-3 py-1 text-sm rounded-md flex items-center gap-1 ${
              selectedCategories.length > 0 
                ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Package className="w-4 h-4" />
            Category
            {selectedCategories.length > 0 && (
              <span className="bg-purple-200 text-purple-800 text-xs px-1 rounded-full">
                {selectedCategories.length}
              </span>
            )}
          </button>
        </div>
      </div>

      <CategoryFilterModal
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        onApply={handleCategoryApply}
        selectedCategories={tempSelectedCategories}
        setSelectedCategories={setTempSelectedCategories}
      />
    </>
  );
};

// IndentItem component
const IndentItem = ({ item, onViewDetails }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "bg-yellow-100 text-yellow-800";
      case "Partially Received":
        return "bg-green-100 text-green-800";
      case "Completed":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getDaysLeftColor = (days) => {
    if (days < 0) return "text-red-600";
    if (days <= 1) return "text-red-500";
    if (days <= 3) return "text-orange-500";
    if (days <= 7) return "text-yellow-500";
    return "text-green-500";
  };

  const getDaysLeftText = (days) => {
    if (days < 0) return "Overdue";
    if (days === 0) return "Due today";
    if (days === 1) return "1 day left";
    return `${days} days left`;
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(item.ReceiptStatus)}`}>
            {item.ReceiptStatus}
          </span>
          <h3 className="text-lg font-semibold">Indent #{item.IndentID}</h3>
        </div>
        <button
          onClick={() => onViewDetails(item)}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
        >
          <Eye className="w-4 h-4" />
          View Details
        </button>
      </div>
      
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <p className="font-medium">Item Name: {item.ItemName}</p>
          <p className="font-medium">Item ID: {item.ItemID}</p>
          <p>Project: {item.ProjectName}</p>
          <p>Created by: {item.Name || item.CreatedByName}</p>
        </div>
        
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-blue-50 p-2 rounded">
              <p className="text-xs text-blue-600">Requested</p>
              <p className="font-bold">{item.RequestedQuantity} {item.UnitName}</p>
            </div>
            <div className="bg-green-50 p-2 rounded">
              <p className="text-xs text-green-600">Received</p>
              <p className="font-bold">{item.QuantityReceived || 0} {item.UnitName}</p>
            </div>
            <div className="bg-orange-50 p-2 rounded">
              <p className="text-xs text-orange-600">Remaining</p>
              <p className="font-bold">{item.RemainingQuantity || (item.RequestedQuantity - (item.QuantityReceived || 0))} {item.UnitName}</p>
            </div>
          </div>
          <div className="bg-gray-50 p-2 rounded">
            <p className="text-xs text-gray-600">Due Date</p>
            <p className="font-medium">{item.CommitedDate}</p>
            <p className={`text-sm font-medium ${getDaysLeftColor(item.daysLeft)}`}>
              {getDaysLeftText(item.daysLeft)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const IndentOpen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const { indentOpen, loading, error } = useSelector((state) => state.dashboard);

  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    days: '',
    status: 'all',
    sortBy: 'IndentID',
    sortOrder: 'asc',
    categories: []
  });

  const [filteredItems, setFilteredItems] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedIndent, setSelectedIndent] = useState(null);
  const [showExport, setShowExport] = useState(false);
  const [isDirectFetch, setIsDirectFetch] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [forceCloseMode, setForceCloseMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showForceCloseDialog, setShowForceCloseDialog] = useState(false);
  const [forceCloseRemark, setForceCloseRemark] = useState("");

  useEffect(() => {
    if (!auth.isAuthenticated) {
      navigate("/login");
      return;
    }
    const initialFilters = {
      ...filters,
      userId: auth.id,
      role: auth.role
    };
    loadData(initialFilters);
  }, [auth.isAuthenticated, navigate]);

  useEffect(() => {
    if (indentOpen && Array.isArray(indentOpen) && !isDirectFetch) {
      console.log("Total indent items:", indentOpen.length);
      filterItems();
    }
  }, [indentOpen, filters.status, selectedCategories, isDirectFetch]);

  const loadData = async (formattedFilters) => {
    try {
      console.log("Loading indent data with filters:", formattedFilters);
      setIsDirectFetch(false);
      const result = await dispatch(fetchIndentOpen({
        ...formattedFilters,
        userId: auth.user?.id,
        role: auth.user?.role
      })).unwrap();
      console.log("Indent data received:", result);
    } catch (err) {
      console.error("Failed to load indent data:", err);
    }
  };

  const filterItems = () => {
    if (!indentOpen || !Array.isArray(indentOpen)) {
      setFilteredItems([]);
      return;
    }

    let filtered = [...indentOpen];
    
    // Apply status filter
    if (filters.status !== "all") {
      filtered = filtered.filter(item => {
        if (filters.status === "pending") return item.ReceiptStatus === "Pending";
        if (filters.status === "partial") return item.ReceiptStatus === "Partially Received";
        return true;
      });
    }
    
    // Apply category filter
    if (selectedCategories.length > 0) {
      filtered = filtered.filter(item => selectedCategories.includes(item.CategoryID));
    }
    
    console.log("Filtered items:", filtered.length);
    setFilteredItems(filtered);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [filterType]: value };
      
      // If date range is set, clear days
      if ((filterType === "startDate" || filterType === "endDate") && value) {
        newFilters.days = "";
      }
      
      // If days is set, clear date range
      if (filterType === "days" && value) {
        newFilters.startDate = "";
        newFilters.endDate = "";
      }
      
      return newFilters;
    });
  };

  const handleCategoryFilter = (categories) => {
    setSelectedCategories(categories);
  };

  const handleViewDetails = (indent) => {
    setSelectedIndent(indent);
  };

  const handleCloseDetails = () => {
    setSelectedIndent(null);
  };

  const handleExport = () => {
    setShowExport(true);
  };

  const handleCloseExport = () => {
    setShowExport(false);
  };

  const handleApplyFilters = () => {
    // Format dates to YYYY-MM-DD before sending
    const formattedFilters = {
      ...filters,
      startDate: filters.startDate ? new Date(filters.startDate).toISOString().split('T')[0] : '',
      endDate: filters.endDate ? new Date(filters.endDate).toISOString().split('T')[0] : ''
    };
    loadData(formattedFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      startDate: "",
      endDate: "",
      days: "",
      status: "all",
      sortBy: "IndentID",
      sortOrder: "asc",
      categories: []
    };
    setFilters(clearedFilters);
    setSelectedCategories([]);
    loadData(clearedFilters);
  };

  const handleSortChange = (sortBy) => {
    // Map UI sort names to database column names
    const newSortBy = sortBy === 'indent' ? 'IndentID' : 'daysLeft';
    const newSortOrder = filters.sortBy === newSortBy && filters.sortOrder === "asc" ? "desc" : "asc";
    
    const updatedFilters = {
      ...filters,
      sortBy: newSortBy,
      sortOrder: newSortOrder
    };
    
    // Update state first
    setFilters(updatedFilters);
    
    // Use the Redux action instead of direct API call
    loadData(updatedFilters);
  };

  const fetchIndentOpenData = async (customFilters = null) => {
    try {
      setIsDirectFetch(true);
      const filtersToUse = customFilters || filters;
      
      const params = new URLSearchParams();
      if (auth.user?.id) params.append('userId', auth.user.id);
      if (auth.user?.role) params.append('role', auth.user.role);
      if (filtersToUse.startDate) params.append('startDate', filtersToUse.startDate);
      if (filtersToUse.endDate) params.append('endDate', filtersToUse.endDate);
      if (filtersToUse.days) params.append('days', filtersToUse.days);
      if (filtersToUse.sortBy) params.append('sortBy', filtersToUse.sortBy);
      if (filtersToUse.sortOrder) params.append('sortOrder', filtersToUse.sortOrder);

      console.log("Fetching direct from API with params:", Object.fromEntries(params));
      const response = await axios.get(`${API_BASE_URL}/dashboard/indent-open?${params.toString()}`);
      console.log("Direct API response:", response.data);
      
      let items = response.data;
      
      // Apply status filter client-side if needed
      if (filtersToUse.status !== "all") {
        items = items.filter(item => {
          if (filtersToUse.status === "pending") return item.ReceiptStatus === "Pending";
          if (filtersToUse.status === "partial") return item.ReceiptStatus === "Partially Received";
          return true;
        });
      }
      
      // Apply category filter client-side
      if (selectedCategories.length > 0) {
        items = items.filter(item => selectedCategories.includes(item.CategoryID));
      }
      
      setFilteredItems(items);
    } catch (error) {
      console.error('Error fetching indent open data:', error);
      toast.error('Failed to fetch indent open data');
    }
  };

  // Handle select/deselect item for force close
  const handleSelectItem = (indentItemId) => {
    setSelectedItems((prev) =>
      prev.includes(indentItemId)
        ? prev.filter((id) => id !== indentItemId)
        : [...prev, indentItemId]
    );
  };

  // Handle force close action
  const handleForceCloseSelected = async () => {
    if (selectedItems.length === 0 || !forceCloseRemark.trim()) return;
    try {
      // Call backend API to force close items
      await axios.post(`${API_BASE_URL}/dashboard/force-close-items`, {
        itemIds: selectedItems,
        remark: forceCloseRemark,
        userId: auth.id,
      });
      toast.success('Items force closed successfully');
      setShowForceCloseDialog(false);
      setSelectedItems([]);
      setForceCloseRemark("");
      setForceCloseMode(false);
      // Immediately refresh Redux slice and UI
      dispatch(fetchIndentOpen({
        ...filters,
        userId: auth.id,
        role: auth.role
      }));
    } catch (error) {
      toast.error('Failed to force close items');
    }
  };

  if (loading.indentOpen && !isDirectFetch) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            <FileDown className="w-4 h-4" />
            Export
          </button>
        </div>

        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Open Indents</h2>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleSortChange("indent")}
                  className={`flex items-center gap-1 px-3 py-1 rounded-md text-sm ${
                    filters.sortBy === "IndentID"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  Indent Number
                  {filters.sortBy === "IndentID" && (
                    <span>{filters.sortOrder === "asc" ? "↑" : "↓"}</span>
                  )}
                </button>
                <button
                  onClick={() => handleSortChange("days")}
                  className={`flex items-center gap-1 px-3 py-1 rounded-md text-sm ${
                    filters.sortBy === "daysLeft"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  Days Left
                  {filters.sortBy === "daysLeft" && (
                    <span>{filters.sortOrder === "asc" ? "↑" : "↓"}</span>
                  )}
                </button>
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
              >
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                <Filter className="w-4 h-4" />
                Filters
              </button>
              <button
                onClick={() => setForceCloseMode((prev) => !prev)}
                className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm ${forceCloseMode ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'} ml-2`}
              >
                {forceCloseMode ? 'Disable Force Close' : 'Enable Force Close'}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="mb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange("startDate", e.target.value)}
                    max={filters.endDate || undefined}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange("endDate", e.target.value)}
                    min={filters.startDate || undefined}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Within Days</label>
                  <div className="flex gap-2">
                    <select
                      value={filters.days}
                      onChange={(e) => {
                        if (e.target.value === "custom") {
                          return;
                        }
                        handleFilterChange("days", e.target.value);
                      }}
                      className="w-1/3 border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="">Select days</option>
                      <option value="1">1 day</option>
                      <option value="3">3 days</option>
                      <option value="7">7 days</option>
                      <option value="14">14 days</option>
                      <option value="30">30 days</option>
                      <option value="custom">Custom</option>
                    </select>
                    <input
                      type="number"
                      min="1"
                      value={filters.days === "custom" ? "" : filters.days}
                      onChange={(e) => handleFilterChange("days", e.target.value)}
                      placeholder="Enter days"
                      className="w-2/3 border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-between">
                <button
                  onClick={handleClearFilters}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
                >
                  <Trash2 className="w-4 h-4" />
                  Clear Filters
                </button>
                <button
                  onClick={handleApplyFilters}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          )}
        </div>

        <ReceiptStatsBar 
          items={filteredItems} 
          onFilterChange={handleFilterChange}
          selectedCategories={selectedCategories}
          onCategoryFilter={handleCategoryFilter}
        />

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          {filteredItems && filteredItems.length > 0 ? (
            filteredItems.map((indent) => (
              <div key={`${indent.IndentID}-${indent.ItemID}`} className="relative">
                <IndentItem
                  item={indent}
                  onViewDetails={handleViewDetails}
                />
                {forceCloseMode && (
                  indent.ForceClosure ? (
                    <button
                      className="absolute top-4 right-4 px-3 py-1 rounded-md text-xs font-semibold border bg-gray-300 text-gray-600 cursor-not-allowed"
                      disabled
                    >
                      Force Closed
                    </button>
                  ) : (
                    <button
                      className={`absolute top-4 right-4 px-3 py-1 rounded-md text-xs font-semibold border ${selectedItems.includes(indent.IndentItemID) ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                      onClick={() => handleSelectItem(indent.IndentItemID)}
                    >
                      {selectedItems.includes(indent.IndentItemID) ? 'Selected' : 'Select'}
                    </button>
                  )
                )}
              </div>
            ))
          ) : (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600">
                {loading.indentOpen && !isDirectFetch ? "Loading indents..." : "No open indents found"}
              </p>
              {!loading.indentOpen && (
                <p className="text-gray-500 mt-2">
                  Try adjusting your filters or check back later
                </p>
              )}
            </div>
          )}
        </div>

        {/* Force Close Selected Button */}
        {forceCloseMode && selectedItems.length > 0 && (
          <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50">
            <button
              className="bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg hover:bg-red-700 text-lg font-semibold"
              onClick={() => setShowForceCloseDialog(true)}
            >
              Force Close Selected ({selectedItems.length})
            </button>
          </div>
        )}

        {/* Force Close Remark Dialog */}
        <Modal
          isOpen={showForceCloseDialog}
          onRequestClose={() => setShowForceCloseDialog(false)}
          className="fixed inset-0 flex items-center justify-center z-50"
          overlayClassName="fixed inset-0 bg-black bg-opacity-50"
          ariaHideApp={false}
        >
          <div className="bg-white rounded-lg p-8 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Force Close Selected Items</h3>
            <textarea
              className="w-full border border-gray-300 rounded-md p-2 mb-4"
              rows={4}
              placeholder="Enter remark for force closing..."
              value={forceCloseRemark}
              onChange={e => setForceCloseRemark(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                onClick={() => setShowForceCloseDialog(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed"
                disabled={!forceCloseRemark.trim()}
                onClick={handleForceCloseSelected}
              >
                Force Close
              </button>
            </div>
          </div>
        </Modal>

        {selectedIndent && (
          <IndentDetails
            indent={selectedIndent}
            onClose={handleCloseDetails}
          />
        )}

        {showExport && (
          <ExportPopup
            alertData={filteredItems}
            onClose={handleCloseExport}
            customFilename="indent-open"
          />
        )}
      </div>
      <Footer />
    </div>
  );
};

export default IndentOpen;